import React, {useState, useEffect, useCallback} from 'react';
import {
  Animated,
  Platform,
  View,
  Image,
  ScrollView,
  TouchableWithoutFeedback,
  TouchableOpacity,
} from 'react-native';

import {SafeAreaView} from 'react-native-safe-area-context';
// eslint-disable-next-line @typescript-eslint/no-var-requires
import { usePullDownRefreshContext } from '../../stores/pullDownRefreshStore';
import {useQueryClient} from 'react-query';

import {
  useRoute,
  useNavigation,
  getFocusedRouteNameFromRoute,
  useFocusEffect,
} from '@react-navigation/native';
import {NavigationKey} from '../../navigations/config';

import {Overlay, makeStyles} from 'react-native-elements'; // rne-netzon replaced

import {LogoLight} from './../../images/Image';

import useStores from '../../stores/useStores';
import {SVGIcon, RNEText} from '../../components';
import {colorPalette} from '../../../core/config/color.config';

import {useSideMenu} from './../../hooks/menu/useSideMenu';

import LogoutConfirmationModal from '../modal/LogoutConfirmationModal';
import SideMenuItem from './SideMenuItem';
import {menuWidth, adminSideMenuItemGroup} from './data/sideMenuItem.data';
import {StyleSheet} from 'react-native';
import {width, height, totalSize} from 'react-native-dimension';
import {fontFamily} from '../../../core/config/font.config';
import useAdminPwaBadge from '../../hooks/notification/useAdminPwaBadge';
import {clearWebAppBadge} from '../../utils/web-app-badge.utils';

const AdminSideMenu = ({currentActiveRouteName}) => {
  const route = useRoute();
  const navigation = useNavigation();
  const {authStore} = useStores();
  
  const queryClient = useQueryClient();
  const canUsePullDown = Platform.OS !== 'web';

  useAdminPwaBadge();

  const {setCanRefresh} = usePullDownRefreshContext();
  
 

  const {widthValue, compressed, toggleMenu} = useSideMenu({
    minValue: menuWidth.compressed,
    maxValue: menuWidth.expanded,
  });

  const [showDialog, setShowDialog] = useState(false);
  const [currentScreen, setCurrentScreen] = useState(
    currentActiveRouteName || NavigationKey.ADMIN_LANDING,
  );

  const navigateTo = (
    navigationKey: NavigationKey,
    shouldSetScreen: boolean = true,
  ) => {
    if (shouldSetScreen) {
      setCurrentScreen(navigationKey);
    }
    navigation.navigate(navigationKey);
  };

  const toggleLogoutConfirmation = () => {
    setShowDialog(true);
  };

  const logout = () => {
    queryClient.clear();
    setShowDialog(false);
    // Clear the saved route on logout so fresh login goes to dashboard
    if (typeof window !== "undefined") {
      localStorage.removeItem("route");
      void clearWebAppBadge();
    }
    authStore.logout();
  };

  useEffect(() => {
    const routeName = getFocusedRouteNameFromRoute(route);
    if (routeName) {
      setCurrentScreen(routeName as NavigationKey);
    }
  }, [route]);

  const [scrollViewHeight, setScrollViewHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useFocusEffect(
    useCallback(() => {
      if (!canUsePullDown) return;

      if (scrollViewHeight > 0 && contentHeight > 0) {
        const isScrollable = contentHeight > scrollViewHeight;

        if (!isScrollable) {
          setCanRefresh(true); // always allow pull-down if not scrollable
        } else {
          setCanRefresh(scrollY <= 0); // only allow when scrolled to top
        }
      }
    }, [scrollViewHeight, contentHeight, scrollY, setCanRefresh]),
  );
  const handleScroll = (e: any) => {
    if (!canUsePullDown) return;

    const y = e.nativeEvent.contentOffset.y;
    setScrollY(y);

    const isScrollable = contentHeight > scrollViewHeight;

    if (!isScrollable) {
      setCanRefresh(true); // still always true if no scroll
    } else {
      setCanRefresh(y <= 0); // only true when back at the very top
    }
  };

  return (
    <SafeAreaView>
      <Overlay
        isVisible={showDialog}
        overlayStyle={sharedStyles.overlayContainer}>
        <LogoutConfirmationModal
          onConfirm={logout}
          onCancel={() => setShowDialog(false)}
        />
      </Overlay>

      <Animated.View
        style={[
          webStyles.mainContainer,
          {
            width: widthValue,
          },
        ]}>
        <View style={webStyles.topWrapper}>
          {!compressed && (
            <View accessibilityRole="button">
              <Image style={webStyles.logo} source={LogoLight} />
            </View>
          )}
          <TouchableWithoutFeedback onPress={toggleMenu}>
            <View
              accessibilityRole="button"
              style={[compressed && {marginLeft: width(0.4)}]}>
              <SVGIcon name="menu" size={totalSize(2)} />
            </View>
          </TouchableWithoutFeedback>
        </View>
        <View style={webStyles.bodyWrapper}>
          <ScrollView
            onScroll={handleScroll}
            onLayout={e => {
              setScrollViewHeight(e.nativeEvent.layout.height);
            }}
            onContentSizeChange={(contentWidth, contentHeight) => {
              setContentHeight(contentHeight);
            }}>
            {adminSideMenuItemGroup.map((menuGroup, groupIndex) => (
              <View key={groupIndex} style={webStyles.contentWrapper}>
                {menuGroup.map((menuItem, itemIndex) => (
                  <SideMenuItem
                    key={itemIndex}
                    label={menuItem.label}
                    iconName={menuItem.iconName}
                    // onPress={() => navigateTo(menuItem.navigationKey)}
                    onPress={() => {
                      // navigateTo(menuItem.navigationKey)
                      navigation.navigate(NavigationKey.MAIN_DRAWER, {
                        screen: menuItem.navigationKey,
                      });
                    }}
                    active={currentScreen === menuItem.navigationKey}
                    hideLabel={compressed}
                  />
                ))}
              </View>
            ))}
          </ScrollView>
        </View>
        <View style={webStyles.profileWrapper}>
          <TouchableOpacity
            activeOpacity={0.7}
            onPress={toggleLogoutConfirmation}>
            <View style={webStyles.profile}>
              <SVGIcon name="download-left" size={totalSize(1.4)} />
              {!compressed && (
                <View style={webStyles.userInfo}>
                  <RNEText
                    label="Logout"
                    variant="button"
                    color="black"
                    style={{
                      fontSize: totalSize(1),
                      fontFamily: 'Quicksand-SemiBold',
                    }}
                  />
                </View>
              )}
            </View>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </SafeAreaView>
  );
};

export default AdminSideMenu;

const userNameStyle = {
  width: Platform.OS === 'web' ? 140 : 100,
};

const webStyles = StyleSheet.create({
  mainContainer: {
    height: '100vh',
    padding: height(2),
    justifyContent: 'space-between',
    backgroundColor: colorPalette.white,
  },
  topWrapper: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignContent: 'center',
    alignItems: 'center',
    // flex: 0.5,
    height: height(8),
    marginBottom: height(5),
    backgroundColor: colorPalette.white,
  },
  logo: {
    width: width(12),
    height: width(5),
    resizeMode: 'contain',
  },
  bodyWrapper: {
    minWidth: 200,
    flex: 2,
    justifyContent: 'space-evenly',
  },
  contentWrapper: {
    marginBottom: height(0),
  },
  contentItem: {
    flexDirection: 'row',
    marginBottom: 20,
    alignItems: 'center',
  },
  subcontentWrapper: {},
  subcontentItem: {
    flexDirection: 'row',
    marginBottom: 20,
    alignItems: 'center',
  },
  profileWrapper: {
    width: '100%',
    height: height(10),
    justifyContent: 'flex-end',
    marginTop: height(2),
    marginBottom: height(2),
    marginLeft: width(1),
    backgroundColor: colorPalette.white,
  },
  profile: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  userInfo: {
    // paddingVertical: 8,
    paddingLeft: width(0.6),
  },
});

const sharedStyles = StyleSheet.create({
  root: {
    height: '100%',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  overlayContainer: {
    borderRadius: 20,
  },
  iconContainer: {
    width: 64,
  },
  labelContainer: {
    flex: 1,
  },
});
